import { createSelector } from "reselect";

import {
  ClearingSaleBuyerStatus,
  ClearingSaleLotStatus,
} from "constants/clearingSaleAttributes";
import { EMPTY_SUMMARY_DATA, PaymentMethod } from "constants/payments";
import { saleLotStatuses } from "constants/sale";

import { EMPTY_ARRAY } from "lib";

import { comparePens } from "lib/auctionPens";
import { uniqueByPropertyFilter } from "lib/filters";

import { selectBuyerBalanceSummaryByBuyerIdLookup } from "./clearingSaleInvoice";
import { selectSaleLotIdsByBuyerIdLookup } from "./indexes";
import { createLookupCombiner, createLookupSelectors } from "./lib";
import { selectPaymentMethodsByBuyerIdLookup } from "./payments";
import { getSaleLots } from "./root";
import { getSaleLotsBySale } from "./saleLots";

export const getBuyerIdsFromSaleLots = createSelector([getSaleLots], saleLots =>
  Object.values(saleLots).map(saleLot => saleLot.buyer_id),
);

export const selectRecentBuyers = createSelector(
  [getSaleLotsBySale],
  saleLots => {
    const recentBuyers = [
      ...saleLots
        // Sort by auction start_pen
        .sort((saleLotA, saleLotB) =>
          comparePens(
            saleLotA.auction_pen?.start_pen || "",
            saleLotB.auction_pen?.start_pen || "",
          ),
        ),
    ]
      .reverse()
      .filter(saleLot => saleLot.status !== saleLotStatuses.NO_SALE)
      // Just want the buyer
      .map(saleLot => saleLot.buyer)
      // And only if it exists.
      .filter(buyer => buyer && buyer.id);
    return recentBuyers.filter(uniqueByPropertyFilter("id"));
  },
);

const buyerStatusReducer = (saleLotIds, saleLots, paymentMethods, balance) => {
  const saleLotStatuses = saleLotIds.map(
    saleLotId => saleLots[saleLotId]?.clearingSaleAttributes?.status,
  );
  const invoiceTotalCents =
    balance.totals.amountExclCents +
    balance.totals.feesCents +
    balance.totals.taxCents;
  // A payment can settle a buyer's balance before the backend gets around to
  // flipping each sale lot's own status to Paid, so treat covering the
  // invoice as fully paid immediately rather than waiting on that to arrive.
  const isFullyPaid =
    saleLotStatuses.every(status => status === ClearingSaleLotStatus.PAID) ||
    (invoiceTotalCents > 0 && balance.totals.dueCents <= 0);

  if (isFullyPaid) {
    const hasAccountPayment = paymentMethods.includes(PaymentMethod.ACCOUNT);
    const hasNonAccountPayment = paymentMethods.some(
      method => method !== PaymentMethod.ACCOUNT,
    );
    // An Account payment alongside real payment methods isn't resolved just
    // because the combined total covers the invoice - the Account portion is
    // still owed, so it's Partially Paid rather than Paid or On Account.
    if (hasAccountPayment && hasNonAccountPayment) {
      return ClearingSaleBuyerStatus.PARTIALLY_PAID;
    }
    return hasAccountPayment
      ? ClearingSaleBuyerStatus.ON_ACCOUNT
      : ClearingSaleBuyerStatus.PAID;
  } else if (
    saleLotStatuses.includes(ClearingSaleLotStatus.PAID) ||
    paymentMethods.length > 0
  ) {
    return ClearingSaleBuyerStatus.PARTIALLY_PAID;
  } else {
    return ClearingSaleBuyerStatus.SOLD;
  }
};

// Sale lots, payment methods, and the balance summary are all keyed by
// buyerId - gather them into one buyerId-keyed lookup first so the final
// selector can use createLookupCombiner like everywhere else in this file.
const selectBuyerStatusInputsByBuyerIdLookup = createSelector(
  [
    selectSaleLotIdsByBuyerIdLookup,
    selectPaymentMethodsByBuyerIdLookup,
    selectBuyerBalanceSummaryByBuyerIdLookup,
  ],
  (saleLotIdsByBuyerId, paymentMethodsByBuyerId, balanceSummaryByBuyerId) =>
    Object.fromEntries(
      Object.entries(saleLotIdsByBuyerId).map(([buyerId, saleLotIds]) => [
        buyerId,
        {
          saleLotIds,
          paymentMethods: paymentMethodsByBuyerId[buyerId] || EMPTY_ARRAY,
          balance: balanceSummaryByBuyerId[buyerId] || EMPTY_SUMMARY_DATA,
        },
      ]),
    ),
);

export const [selectBuyerStatusByBuyerIdLookup, getBuyerStatusByBuyerId] =
  createLookupSelectors(
    [selectBuyerStatusInputsByBuyerIdLookup, getSaleLots],
    createLookupCombiner(({ saleLotIds, paymentMethods, balance }, saleLots) =>
      buyerStatusReducer(saleLotIds, saleLots, paymentMethods, balance),
    ),
  );
